/**
 * CvPeopleManager (web): invite collaborators by email, list access grants, and
 * revoke. Extracted from CVSharePanel so "People" can live as its own entry in
 * the share dialog.
 */
import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { emailSchema } from '@tabbio/utils';
import type { CvTargetKey } from '../../lib/cv-collaboration-api';
import { buildCvAccessInviteUrl } from '../../lib/cv-share-url';
import {
  useCvAccessGrants,
  useCreateCvAccessGrant,
  useRevokeCvAccessGrant,
} from '../../hooks/useCvCollaborationApi';
import { useAuth } from '../../hooks/useAuth';
import { MaterialSymbol } from '../icons/MaterialSymbol';

const normalizeEmail = (value: string | null | undefined) => value?.trim().toLowerCase() ?? '';

export function CvPeopleManager({ target }: { target: CvTargetKey }) {
  const { t } = useTranslation('seeker');
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [lastInviteUrl, setLastInviteUrl] = useState<string | null>(null);
  const [lastInvitedEmail, setLastInvitedEmail] = useState<string | null>(null);
  const [lastInvitedStatus, setLastInvitedStatus] = useState<string | null>(null);
  const [lastMessage, setLastMessage] = useState<string | null>(null);
  const [revokingGrantId, setRevokingGrantId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const accessGrants = useCvAccessGrants(target);
  const createAccess = useCreateCvAccessGrant(target);
  const revokeAccess = useRevokeCvAccessGrant(target);
  const grants = accessGrants.data ?? [];
  const currentUserEmail = normalizeEmail(user?.email);
  const enteredEmail = normalizeEmail(email);
  const inviteDisabled = !email.trim() || createAccess.isPending;

  const invite = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    const parsed = emailSchema.safeParse(trimmed);
    if (!parsed.success) {
      setLastMessage(t('cv.sharePanel.people.invalidEmail'));
      return;
    }
    if (currentUserEmail && enteredEmail === currentUserEmail) {
      setLastMessage(t('cv.sharePanel.people.selfInvite'));
      return;
    }
    const existing = grants.find((grant) => {
      if (grant.status === 'revoked') return false;
      return normalizeEmail(grant.granteeEmail) === enteredEmail;
    });
    const duplicateStatus = existing?.status ?? (lastInvitedEmail === enteredEmail ? lastInvitedStatus : null);
    if (duplicateStatus) {
      setLastMessage(
        t(
          duplicateStatus === 'pending'
            ? 'cv.sharePanel.people.alreadyPending'
            : 'cv.sharePanel.people.alreadyHasAccess',
          { email: existing?.granteeEmail ?? trimmed },
        ),
      );
      return;
    }
    try {
      const result = await createAccess.mutateAsync({ granteeEmail: trimmed, role: 'editor' });
      if (result.inviteToken) {
        setLastInviteUrl(buildCvAccessInviteUrl(result.inviteToken, result.inviteUrl));
        setLastMessage(t('cv.sharePanel.people.inviteLinkCreated'));
      } else {
        setLastInviteUrl(null);
        setLastMessage(
          t('cv.sharePanel.people.grantedDirect', {
            email: result.grant.granteeEmail ?? trimmed,
          }),
        );
      }
      setLastInvitedEmail(enteredEmail);
      setLastInvitedStatus(result.grant.status);
      setEmail('');
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      setLastMessage(
        message.toLowerCase().includes('already own')
          ? t('cv.sharePanel.people.selfInvite')
          : t('cv.sharePanel.people.inviteFailed'),
      );
    }
  };

  const revoke = async (id: string) => {
    setRevokingGrantId(id);
    try {
      await revokeAccess.mutateAsync(id);
      setLastInviteUrl(null);
      setLastInvitedEmail(null);
      setLastInvitedStatus(null);
      setLastMessage(t('cv.sharePanel.people.revoked'));
    } finally {
      setRevokingGrantId(null);
    }
  };

  const copyInvite = async () => {
    if (!lastInviteUrl) return;
    await navigator.clipboard.writeText(lastInviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div style={wrap}>
      <div style={inviteRow}>
        <div style={{ position: 'relative', minWidth: 0 }}>
          <MaterialSymbol name="mail" size={18} style={inputIcon} />
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('cv.sharePanel.people.emailPlaceholder')}
            style={input}
            disabled={createAccess.isPending}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !inviteDisabled) {
                event.preventDefault();
                void invite();
              }
            }}
          />
        </div>
        <button type="button" onClick={invite} disabled={inviteDisabled} style={primaryBtn(inviteDisabled)}>
          {createAccess.isPending ? t('cv.sharePanel.people.inviting') : t('cv.sharePanel.people.invite')}
        </button>
      </div>

      <div style={copyBar}>
        <span style={copyValue}>
          <span style={copyLabel}>{t('cv.sharePanel.people.accessLink')}</span>
          <span style={copyText}>
            {lastInviteUrl ? lastInviteUrl.replace(/^https?:\/\//, '') : t('cv.sharePanel.people.accessLinkEmpty')}
          </span>
        </span>
        <button type="button" onClick={copyInvite} disabled={!lastInviteUrl} style={copyBtn(!lastInviteUrl)}>
            <MaterialSymbol name={copied ? 'check' : 'content_copy'} size={18} />
        </button>
      </div>

      {lastMessage ? <div style={notice}>{lastMessage}</div> : null}

      {accessGrants.isError ? <div style={empty}>{t('cv.sharePanel.people.loadFailed')}</div> : null}

      <div style={peopleList}>
        {grants.map((grant) => (
          <div key={grant.id} style={personRow}>
            <span style={{ minWidth: 0 }}>
              <span style={personName}>
                {grant.granteeEmail ?? grant.granteeUserId ?? t('cv.sharePanel.people.invitedUser')}
              </span>
              <span style={personStatus}>
                {t(`cv.sharePanel.people.status.${grant.status}`, { defaultValue: grant.status })}
              </span>
            </span>
            <button type="button" onClick={() => revoke(grant.id)} disabled={revokingGrantId === grant.id} style={revokeBtn}>
              {revokingGrantId === grant.id ? t('cv.sharePanel.people.revoking') : t('cv.sharePanel.people.revoke')}
            </button>
          </div>
        ))}
        {!accessGrants.isError && grants.length === 0 ? <div style={empty}>{t('cv.sharePanel.people.empty')}</div> : null}
      </div>
    </div>
  );
}

const wrap: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  fontFamily: "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
  color: 'var(--text-primary)',
};
const inviteRow: CSSProperties = { display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 10, alignItems: 'center' };
const inputIcon: CSSProperties = {
  position: 'absolute',
  insetBlockStart: '50%',
  insetInlineStart: 16,
  transform: 'translateY(-50%)',
  color: 'var(--text-secondary)',
  pointerEvents: 'none',
};
const input: CSSProperties = {
  width: '100%',
  height: 50,
  borderRadius: 9999,
  border: '1px solid var(--input-border)',
  paddingInlineStart: 46,
  paddingInlineEnd: 18,
  fontSize: 15,
  color: 'var(--text-primary)',
  background: 'var(--input-bg)',
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};
const primaryBtn = (disabled: boolean): CSSProperties => ({
  minHeight: 50,
  borderRadius: 9999,
  border: 0,
  paddingInline: 18,
  background: 'var(--text-primary)',
  color: 'var(--bg)',
  fontSize: 14,
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.4 : 1,
  whiteSpace: 'nowrap',
});
const copyBar: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minHeight: 48,
  padding: '4px 6px 4px 14px',
  borderRadius: 12,
  border: '1px solid var(--input-border)',
  background: 'var(--input-bg)',
};
const copyValue: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};
const copyLabel: CSSProperties = {
  fontSize: 11,
  lineHeight: '14px',
  fontWeight: 500,
  color: 'var(--text-tertiary)',
};
const copyText: CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 13,
  color: 'var(--text-secondary)',
};
const copyBtn = (disabled: boolean): CSSProperties => ({
  width: 38,
  height: 38,
  borderRadius: 9999,
  border: 0,
  background: 'transparent',
  color: 'var(--text-secondary)',
  cursor: disabled ? 'default' : 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: disabled ? 0.35 : 1,
});
const notice: CSSProperties = {
  borderRadius: 12,
  padding: '10px 14px',
  background: 'var(--input-bg)',
  border: '1px solid var(--input-border)',
  fontSize: 13,
  lineHeight: '18px',
  color: 'var(--text-secondary)',
};
const peopleList: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 190,
  overflowY: 'auto',
  paddingInlineEnd: 2,
};
const personRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  padding: '12px 0',
  borderTop: '1px solid var(--input-border)',
};
const personName: CSSProperties = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 14,
  fontWeight: 500,
};
const personStatus: CSSProperties = { display: 'block', marginTop: 2, fontSize: 12, color: 'var(--text-tertiary)' };
const revokeBtn: CSSProperties = {
  height: 32,
  paddingInline: 12,
  borderRadius: 9999,
  border: 0,
  cursor: 'pointer',
  background: 'rgba(217,48,37,0.1)',
  color: '#d93025',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: 'inherit',
  flexShrink: 0,
};
const empty: CSSProperties = { fontSize: 13, color: 'var(--text-tertiary)' };
